import * as React from "react";
import { ChangeEvent, MouseEvent, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Paper,
  Typography,
  Toolbar,
  Button,
  TextField,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import { visuallyHidden } from "@mui/utils";
import { getComparator, Order, stableSort } from "../../utils/Table";
import { IIdentifiableStaticData, IDiffResponse, IDiffItem } from "../../services/backend/GameConfigService";

type ItemsTableHeadProperties = keyof IIdentifiableStaticData;

interface HeadCell {
  id: ItemsTableHeadProperties;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "sdId",
    label: "SDId",
    padding: "none",
  },
  {
    id: "id",
    label: "ID",
  },
];

interface ItemsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: ItemsTableHeadProperties) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const ItemsTableHead = ({ order, orderBy, onRequestSort }: ItemsTableHeadProps) => {
  const createSortHandler = (property: ItemsTableHeadProperties) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
            style={index === 0 ? { paddingLeft: "16px" } : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell padding="normal" />
      </TableRow>
    </TableHead>
  );
};

interface GameConfigItemsTableToolbarProps {
  onRefreshButtonClick: () => void;
  itemsCount: number;
  filterQuery: string;
  setFilterQuery: (value: string) => void;
  diffData?: IDiffResponse;
}

const GameConfigItemsTableToolbar = ({
  onRefreshButtonClick,
  itemsCount,
  filterQuery,
  setFilterQuery,
  diffData,
}: GameConfigItemsTableToolbarProps) => {
  const hasChanges = diffData && (diffData.added.length > 0 || diffData.removed.length > 0 || diffData.updated.length > 0);

  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <Typography variant="h6" gutterBottom style={{ marginRight: "16px", marginTop: "6px" }}>
        Game Config Items ({itemsCount})
      </Typography>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshButtonClick}>
        Refresh
      </Button>
      <TextField
        id="outlined-search"
        label="Search items"
        type="search"
        size="small"
        onChange={(e) => setFilterQuery(e.target.value)}
        sx={{ width: "360px", marginLeft: 2 }}
        value={filterQuery}
      />
      {hasChanges && (
        <Box sx={{ marginLeft: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Changes:
          </Typography>
          {diffData.added.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(76, 175, 80, 0.3)",
                  border: "1px solid rgba(76, 175, 80, 0.8)",
                }}
              />
              <Typography variant="caption">Added ({diffData.added.length})</Typography>
            </Box>
          )}
          {diffData.updated.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(255, 193, 7, 0.3)",
                  border: "1px solid rgba(255, 193, 7, 0.8)",
                }}
              />
              <Typography variant="caption">Updated ({diffData.updated.length})</Typography>
            </Box>
          )}
          {diffData.removed.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(244, 67, 54, 0.3)",
                  border: "1px solid rgba(244, 67, 54, 0.8)",
                }}
              />
              <Typography variant="caption">Removed ({diffData.removed.length})</Typography>
            </Box>
          )}
        </Box>
      )}
    </Toolbar>
  );
};

// Helper function to determine row background color based on diff status
const getRowBackgroundColor = (item: IIdentifiableStaticData, diffData?: IDiffResponse): string => {
  if (!diffData) return "transparent";

  if (diffData.added.some((diffItem) => diffItem.sdId === item.sdId)) {
    return "rgba(76, 175, 80, 0.1)"; // Light green
  }

  if (diffData.removed.some((diffItem) => diffItem.sdId === item.sdId)) {
    return "rgba(244, 67, 54, 0.1)"; // Light red
  }

  if (diffData.updated.some((diffItem) => diffItem.sdId === item.sdId)) {
    return "rgba(255, 193, 7, 0.1)"; // Light yellow
  }

  return "transparent"; // No changes
};

export interface GameConfigItemsTableProps {
  items: IIdentifiableStaticData[];
  diffData?: IDiffResponse;
  onViewMenuClick: (item: IIdentifiableStaticData, diffItem?: IDiffItem) => void;
  onDeleteMenuClick: (item: IIdentifiableStaticData) => void;
  onRefreshButtonClick: () => void;
}

export const GameConfigItemsTable = ({
  items,
  diffData,
  onViewMenuClick,
  onDeleteMenuClick,
  onRefreshButtonClick,
}: GameConfigItemsTableProps) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<ItemsTableHeadProperties>("sdId");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<IIdentifiableStaticData | undefined>(undefined);
  const [filterQuery, setFilterQuery] = useState<string>("");

  const handleRequestSort = (event: MouseEvent<unknown>, property: ItemsTableHeadProperties) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>, item: IIdentifiableStaticData) => {
    event.stopPropagation();
    setMenuAnchor(event.target as Element);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(undefined);
  };

  const handleRowClick = (item: IIdentifiableStaticData) => {
    const diffItem = diffData?.updated.find((diffItem) => diffItem.sdId === item.sdId);
    onViewMenuClick(item, diffItem);
  };

  const handleViewClick = () => {
    if (selectedItem) {
      const diffItem = diffData?.updated.find((diffItem) => diffItem.sdId === selectedItem.sdId);
      onViewMenuClick(selectedItem, diffItem);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (selectedItem) {
      onDeleteMenuClick(selectedItem);
    }
    handleMenuClose();
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - items.length) : 0;

  const filteredRows = useMemo(() => {
    const query = filterQuery.toLowerCase();
    return items.filter((row) => {
      return row.id.toLowerCase().includes(query) || row.sdId.toLowerCase().includes(query);
    });
  }, [items, filterQuery]);

  const visibleRows = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return stableSort(filteredRows as any, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <GameConfigItemsTableToolbar
          onRefreshButtonClick={onRefreshButtonClick}
          itemsCount={filteredRows.length}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          diffData={diffData}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <ItemsTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={filteredRows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `items-table-row-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    sx={{ cursor: "pointer", backgroundColor: getRowBackgroundColor(row, diffData) }}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      style={{ fontFamily: "monospace", paddingLeft: "16px" }}
                    >
                      {row.sdId}
                    </TableCell>
                    <TableCell align="left" style={{ fontFamily: "monospace" }}>
                      {row.id}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      <Menu anchorEl={menuAnchor} keepMounted open={menuAnchor !== undefined} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewClick} disabled={false}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">View</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} disabled={selectedItem === undefined}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Delete</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};
